import { apiFetch } from '@/lib/api/client'
import { useMockApi } from '@/lib/api/client'

export type ExportFormat = 'markdown' | 'html' | 'pdf' | 'docx'
export type ExportSourceType = 'session' | 'compare_batch'

export interface CreateExportRequest {
  exportType: ExportSourceType
  sourceId: string
  format: ExportFormat
}

export interface CreateBatchExportRequest {
  exportType: ExportSourceType
  sourceIds: string[]
  format: ExportFormat
}

export interface ExportJob {
  exportId: string
  status: string
  downloadUrl?: string
}

export interface ExportsApi {
  createExport(request: CreateExportRequest): Promise<ExportJob>
  createBatchExport(request: CreateBatchExportRequest): Promise<ExportJob>
  waitForReady(exportId: string, maxAttempts?: number): Promise<ExportJob>
  download(exportId: string): Promise<void>
}

class MockExportsApi implements ExportsApi {
  async createExport(request: CreateExportRequest): Promise<ExportJob> {
    const exportId = `export-${crypto.randomUUID()}`
    return {
      exportId,
      status: 'ready',
      downloadUrl: `/api/v1/exports/${exportId}/download`,
    }
  }

  async createBatchExport(request: CreateBatchExportRequest): Promise<ExportJob> {
    const exportId = `export-batch-${crypto.randomUUID()}`
    return {
      exportId,
      status: 'ready',
      downloadUrl: `/api/v1/exports/${exportId}/download`,
    }
  }

  async waitForReady(exportId: string): Promise<ExportJob> {
    return {
      exportId,
      status: 'ready',
      downloadUrl: `/api/v1/exports/${exportId}/download`,
    }
  }

  async download(exportId: string): Promise<void> {
    const blob = new Blob(
      [`# Mock export\n\nSource: ${exportId}\nGenerated at ${new Date().toISOString()}`],
      { type: 'text/markdown' },
    )
    triggerDownload(blob, `modelscout-export-${exportId}.md`)
  }
}

class HttpExportsApi implements ExportsApi {
  async createExport(request: CreateExportRequest): Promise<ExportJob> {
    const body = await apiFetch<{
      export_id: string
      status: string
    }>('/api/v1/exports', {
      method: 'POST',
      body: JSON.stringify({
        export_type: request.exportType,
        source_id: request.sourceId,
        format: request.format,
      }),
    })

    return {
      exportId: body.export_id,
      status: body.status,
    }
  }

  async createBatchExport(request: CreateBatchExportRequest): Promise<ExportJob> {
    const body = await apiFetch<{
      export_id: string
      status: string
    }>('/api/v1/exports/batch', {
      method: 'POST',
      body: JSON.stringify({
        export_type: request.exportType,
        source_ids: request.sourceIds,
        format: request.format,
      }),
    })

    return {
      exportId: body.export_id,
      status: body.status,
    }
  }

  async waitForReady(exportId: string, maxAttempts = 20): Promise<ExportJob> {
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const body = await apiFetch<{
        id: string
        status: string
        download_url?: string
      }>(`/api/v1/exports/${exportId}`)

      if (body.status === 'ready' || body.status === 'failed') {
        return {
          exportId: body.id,
          status: body.status,
          downloadUrl: body.download_url,
        }
      }

      await new Promise((resolve) => setTimeout(resolve, 250))
    }

    throw new Error('export_timeout')
  }

  async download(exportId: string): Promise<void> {
    const response = await fetch(`/api/v1/exports/${exportId}/download`, {
      credentials: 'include',
    })
    if (!response.ok) throw new Error('export_download_failed')

    const blob = await response.blob()
    const disposition = response.headers.get('Content-Disposition') ?? ''
    const match = disposition.match(/filename="?([^"]+)"?/)
    const filename = match?.[1] ?? `modelscout-export-${exportId}`

    triggerDownload(blob, filename)
  }
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

export const exportsApi: ExportsApi = useMockApi() ? new MockExportsApi() : new HttpExportsApi()
